// CORE
import { DateTime } from 'luxon';
import React, { useCallback, useMemo, useState, useEffect } from 'react';

// MUI
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import DialogContent from '@mui/material/DialogContent';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { makeStyles } from 'tss-react/mui';

// DATA
import type { accountsTypes } from 'app-components/flux/accounts';
import { balanceSelectors } from 'app-components/flux/calculated-balances';
import { dateSelectors } from 'app-components/flux/date';
import type { Goal } from 'app-components/flux/goals/goalsTypes';
import { goalsActions, goalsSelectors, goalsUtils } from 'app-components/flux/goals';
import { rootUiActions } from 'app-components/flux/root-ui';
import { tracker } from 'app-components/utils/core';
import { isDevMode } from 'app-components/utils/core/config';
import { useAppDispatch, useAppSelector } from 'app-components/hooks/redux';

// Shared
import StdDialog from 'components/Dialogs/StdDialog';
import StdDialogActions from 'components/Dialogs/StdDialogActions';
import AmountField from 'components/QuickenControls/AmountField';
import ZeroStateView from 'components/ZeroStateView';
import useTxnActions from 'data/transactionActions/useTxnActions';

import QIllustration from 'components/QIllustration';
import DevMenuButton from 'components/DevMenuButton';
import { dispatchGeneratorDialog } from 'components/Transactions/GeneratorDialog/dispatcher';

const useStyles = makeStyles()((theme) => ({
  contentContainer: {
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3),
  },
  amountBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    background: theme.palette.info.light,
    borderRadius: theme.shape.borderRadius * 2,
    padding: theme.spacing(1),
    minWidth: 142,
    flexGrow: 1,
  },
  longAmountBox: {
    background: theme.palette.info.light,
    borderRadius: theme.shape.borderRadius * 2,
    padding: theme.spacing(1),
    marginRight: theme.spacing(1),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minWidth: 262,
    maxWidth: 288,
    flexGrow: 3,
  },
  subAmountBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    margin: `0 ${theme.spacing(1)}`,
  },
  contributeAmount: {
    textAlign: 'left',
    maxWidth: 212,
  },
  accountName: {
    maxWidth: 229,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
}));

function ContributeToGoal(props: {
  goal: Goal;
  context?: string;
  selectedDate?: string | undefined | null;
  amount?: number;
  onResult?: (result: { success: boolean; error?: string }) => void;
}) {
  const { goal, context, selectedDate, amount, onResult } = props;
  const { classes } = useStyles();

  const currentDate = useAppSelector(dateSelectors.getCurrentDate).startOf('month');

  const date = selectedDate ? DateTime.fromISO(selectedDate) : currentDate;
  const { month, year } = date;

  const dispatch = useAppDispatch();

  const allBalances: any = useAppSelector(balanceSelectors.getBalancesByAccountIds);
  const possibleContributionAccounts = useAppSelector(goalsSelectors.getPossibleContributionAccounts);
  const getGoalTxnsSelectorProps = useMemo(
    () => ({
      acctId: goal.accountId || '',
      month,
      year,
    }),
    [goal.accountId, month, year],
  );
  const goalTxnsforSelectedMonth = useAppSelector((state) =>
    goalsSelectors.getGoalTxnsForIdAndDate(state, getGoalTxnsSelectorProps),
  );

  // ## - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // ##           State Values
  // ## - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const [contributeFromAccount, setContributeFromAccount] = useState<accountsTypes.Account | null>(null);
  const [contributionAmount, setContributionAmount] = useState(0);
  const contributionMaximum = allBalances.get(contributeFromAccount?.id)?.availableBalance;
  const amountIsNumber = !Number.isNaN(contributionAmount);

  const [submissionAttempted, setSubmissionAttempted] = useState(false);

  const [awaitingContribution, setAwaitingContribution] = useState(false);
  const [victory, setVictory] = useState(false);
  const settledRef = React.useRef(false);

  const settleOnce = useCallback(
    (result: { success: boolean; error?: string }) => {
      if (settledRef.current) return;
      settledRef.current = true;
      onResult?.(result);
    },
    [onResult],
  );

  // ## - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // ##           Form Computations
  // ## - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const amountError = Boolean(
    contributionAmount
      ? !amountIsNumber || contributionAmount <= 0 || contributionAmount > contributionMaximum
      : submissionAttempted,
  );

  let amountWarning = null;
  if (amountError) {
    if (contributionAmount > contributionMaximum) {
      amountWarning = `You can't contribute more than your account balance`;
    } else if (contributionAmount < 0) {
      amountWarning = 'Cannot contribute negative amounts';
    } else if (!contributionAmount || contributionAmount === 0) {
      amountWarning = 'required';
    }
  }

  const formError = Boolean(
    !amountIsNumber || !contributeFromAccount || contributionAmount <= 0 || contributionAmount > contributionMaximum,
  );

  const { saveTransactions } = useTxnActions({
    wrapperId: context || 'ContributeToGoal',
    robotMode: true,
    callback: () => {
      tracker.track(tracker.events.addTo, {
        accountType: contributeFromAccount?.type,
        accountSubType: contributeFromAccount?.subType,
      });
      setAwaitingContribution(false);
      setVictory(true);
      settleOnce({ success: true });
    },
  });

  // ## - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // ##           Callbacks
  // ## - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const onHandleClose = useCallback(
    (_event?: any, _reason?: string) => {
      settleOnce({ success: false, error: 'User cancelled' });
      dispatch(rootUiActions.removeDialog());
    },
    [dispatch, settleOnce],
  );

  const handleSubmit = useCallback(() => {
    if (formError) {
      setSubmissionAttempted(true);
      return;
    }
    if (contributeFromAccount?.id && goal?.accountId && contributionAmount > 0) {
      setAwaitingContribution(true);
      const transferTxns = goalsUtils.makeGoalTransactions(
        goal,
        contributeFromAccount.id,
        contributionAmount,
        undefined,
        date?.toISODate(),
      );
      saveTransactions(transferTxns);
      setTimeout(() => {
        contributeFromAccount.id && dispatch(goalsActions.refreshGoalBalance(contributeFromAccount.id));
      }, 4000);
    } else {
      setSubmissionAttempted(true);
    }
  }, [contributeFromAccount, contributionAmount, date, goal, saveTransactions, formError, dispatch]);

  const handleAccountSelectChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setContributeFromAccount(
        possibleContributionAccounts.get(e.target.value) ?? possibleContributionAccounts?.first() ?? null,
      );
    },
    [possibleContributionAccounts],
  );

  const handleContributionAmountChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setContributionAmount(Number(e.target.value));
  }, []);

  useEffect(() => {
    if (typeof amount !== 'number' || Number.isNaN(amount)) return;
    setContributionAmount(amount);
  }, [amount]);

  // ## - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // ##           Effects (auto-close)
  // ## - - - - - - - - - - - - - - - - - - - - - - - - - - -

  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined;
    if (awaitingContribution) {
      // close 6 seconds after submitting contribution request
      timeout = setTimeout(onHandleClose, 6000);
    }
    if (victory) {
      // auto close in 3 seconds on victory
      timeout = setTimeout(onHandleClose, 3000);
    }
    return () => clearTimeout(timeout);
  }, [awaitingContribution, victory, onHandleClose]);

  // ## - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // ##           Memoized renders
  // ## - - - - - - - - - - - - - - - - - - - - - - - - - - -

  const selectedMonthsContribution = useMemo(
    () =>
      goalTxnsforSelectedMonth.size > 0
        ? goalTxnsforSelectedMonth
            .map((txn) =>
              txn.subtype !== 'GOAL_SPENT' && txn.subtype !== 'GOAL_STARTING_AMOUNT' ? Number(txn.amount) : 0,
            )
            .reduce((sum, amt) => sum + amt, 0)
        : 0,
    [goalTxnsforSelectedMonth],
  );

  const dialogContent = useMemo(() => {
    if (awaitingContribution) {
      return (
        <Box display='flex' justifyContent='center' alignItems='center' minHeight={300}>
          <CircularProgress size={100} />
        </Box>
      );
    }

    if (victory) {
      return (
        <Box display='flex' justifyContent='center' minHeight={300} pt={4}>
          <ZeroStateView qIllustration='starHappy' subtitle='You’re one step closer to reaching your goal!' />
        </Box>
      );
    }

    const isTodayMonth = date.hasSame(currentDate, 'month');
    const monthString = isTodayMonth ? 'Saved this month' : `Saved in ${date.toFormat('MMMM')}`;

    let leftToSave = Number(goal.targetAmount) - Number(goal.savedSoFar);
    if (leftToSave < 0) {
      leftToSave = 0;
    }

    return (
      <Box display='flex' gap={1}>
        <QIllustration name='moneyWalletIn' size='md' />
        <Box display='flex' flexDirection='column' gap={2} mb={1}>
          <Typography variant='titleRegular'>{goal.name}</Typography>

          <Box display='flex' mb={1} minWidth='416px'>
            <div className={classes.longAmountBox}>
              <div className={classes.subAmountBox}>
                <Typography variant='paragraphRegular'>{monthString}</Typography>
                <AmountField
                  id='saved_this_month_amount'
                  variant='titleRegular'
                  editable={false}
                  value={selectedMonthsContribution}
                />
              </div>
              <div className={classes.subAmountBox}>
                <Typography variant='paragraphRegular'>Monthly Target</Typography>
                <AmountField
                  id='monthly_target_amount'
                  variant='titleRegular'
                  editable={false}
                  value={goal.contributionAmount}
                />
              </div>
            </div>
            <div className={classes.amountBox}>
              <Typography variant='paragraphRegular'>Left to save</Typography>
              <AmountField id='contribute_left_to_save' variant='titleRegular' editable={false} value={leftToSave} />
            </div>
          </Box>

          <AmountField
            label='Amount to contribute'
            amountType='amount'
            showAmountAdornment
            currencySymbol='USD'
            textFieldVariant='filled'
            editable
            id='amount_to_contribute'
            onChange={handleContributionAmountChange}
            value={contributionAmount}
            className={classes.contributeAmount}
            error={amountError}
            helperText={amountWarning}
          />

          <TextField
            value={contributeFromAccount?.id ?? ''}
            defaultValue={contributeFromAccount?.name ?? ''}
            select
            id='contribute-account-select'
            label='Take from account'
            variant='filled'
            onChange={handleAccountSelectChange}
            error={contributionAmount > contributionMaximum}
          >
            {possibleContributionAccounts.valueSeq?.().map?.((acct) =>
              acct ? (
                <MenuItem key={acct.id ?? acct.clientId} value={acct.id ?? acct.clientId}>
                  <Box display='flex' alignItems='center' justifyContent='space-between' width='100%'>
                    <div className={classes.accountName}>{acct.name}</div>
                    <div>
                      Balance&nbsp;
                      <AmountField editable={false} value={allBalances.get(acct.id)?.availableBalance || 0} />
                    </div>
                  </Box>
                </MenuItem>
              ) : (
                <MenuItem key='deleted-acct' value='Deleted account'>
                  Deleted account
                </MenuItem>
              ),
            )}
          </TextField>
        </Box>
      </Box>
    );
  }, [
    awaitingContribution,
    date,
    currentDate,
    classes,
    selectedMonthsContribution,
    goal,
    contributeFromAccount,
    handleAccountSelectChange,
    possibleContributionAccounts,
    handleContributionAmountChange,
    contributionAmount,
    contributionMaximum,
    allBalances,
    amountError,
    amountWarning,
    victory,
  ]);

  // ## - - - - - - - - - - - - - - - - - - - - - - - - - - -
  // ##           return
  // ## - - - - - - - - - - - - - - - - - - - - - - - - - - -

  return (
    <StdDialog
      onClose={onHandleClose}
      open
      sharedcomponentid='CONTRIBUTE_TO_GOAL_DIALOG'
      showCloseButton
      size='sm'
      title='Contribute to goal'
    >
      <DialogContent className={classes.contentContainer}>{dialogContent}</DialogContent>
      {!(victory || awaitingContribution) && (
        <StdDialogActions
          primary={{
            children: 'Contribute',
            onClick: handleSubmit,
            id: 'submit-contribute-to-goal',
            'aria-label': 'contribute',
            tooltipText: formError ? 'Form fields need attention' : undefined,
          }}
          secondaryNode={
            isDevMode() && (
              <DevMenuButton id='generate-contributions-dev-menu'>
                <MenuItem
                  onClick={() => {
                    const txns = goalsUtils.makeGoalTransactions(
                      goal,
                      contributeFromAccount?.id || '',
                      contributionAmount,
                      undefined,
                      date?.toISODate(),
                    );
                    dispatchGeneratorDialog({ txns });
                  }}
                >
                  Generate Contributions
                </MenuItem>
              </DevMenuButton>
            )
          }
        />
      )}
    </StdDialog>
  );
}

export default ContributeToGoal;
